import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { CtaBand } from "@/components/CtaBand";
import { EASE, Reveal } from "@/components/Reveal";
import { POSTS } from "@/data/content";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const TAGS = ["All", ...new Set(POSTS.map((p) => p.tag))];

export default function Insights() {
  const [tag, setTag] = useState("All");
  const [post, setPost] = useState(null);
  const visible = POSTS.filter((p) => tag === "All" || p.tag === tag);

  return (
    <div data-testid="insights-page">
      <Seo
        title="Insights — Vigarbiz"
        siteName="Vigarbiz"
        description="Field notes from the growth floor: measurement, programmatic SEO, paid creative systems, brand gravity, AI workflows and content compounding."
      />
      <PageHeader
        overline="Insights"
        lines={["Field notes", "From the", <span key="g" className="text-brand">Growth floor.</span>]}
        description="No thought-leadership soup. Everything here is something we shipped, measured and would bet client money on again."
      />

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex flex-wrap gap-2" data-testid="insights-filters">
            {TAGS.map((t) => (
              <button
                key={t}
                data-testid={`insights-filter-${t.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setTag(t)}
                className={`rounded-full border px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
                  tag === t
                    ? "border-brand bg-brand text-ink"
                    : "border-white/15 text-muted2 hover:border-brand/50 hover:text-white"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {visible.map((p) => (
              <motion.button
                layout
                key={p.slug}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.35, ease: EASE }}
                onClick={() => setPost(p)}
                data-testid={`post-card-${p.slug}`}
                className="glow-card group overflow-hidden rounded-2xl border border-white/10 bg-surface text-left"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={p.cover}
                    alt={p.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.2em]">
                    <span className="text-brand">{p.tag}</span>
                    <span className="text-dim">{p.date} · {p.readTime}</span>
                  </div>
                  <h3 className="mt-3 font-display text-lg font-bold leading-snug text-white transition-colors group-hover:text-brand">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted2">{p.excerpt}</p>
                  <span className="mt-4 inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.2em] text-dim transition-colors group-hover:text-brand">
                    Read article <ArrowUpRight className="h-3 w-3" />
                  </span>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      <Dialog open={!!post} onOpenChange={(o) => !o && setPost(null)}>
        <DialogContent
          className="max-h-[85vh] overflow-y-auto border-white/10 bg-surface text-white sm:max-w-3xl"
          data-testid="insights-reader-modal"
        >
          {post && (
            <>
              <div className="-mx-6 -mt-6 mb-6 h-56 overflow-hidden sm:h-64">
                <img src={post.cover} alt={post.title} className="h-full w-full object-cover" />
              </div>
              <DialogHeader>
                <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em]">
                  <span className="text-brand">{post.tag}</span>
                  <span className="text-dim">{post.date} · {post.readTime} read</span>
                </div>
                <DialogTitle className="mt-3 font-display text-2xl font-bold leading-tight sm:text-3xl">
                  {post.title}
                </DialogTitle>
              </DialogHeader>
              <div className="mt-4 space-y-5">
                {post.body.map((para, i) => (
                  <p key={i} className="text-base leading-relaxed text-muted2">
                    {para}
                  </p>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <CtaBand title="Reading is free. Results aren't." sub="If any of these hit a nerve, imagine what the full machine does to your funnel." />
    </div>
  );
}
